import type { TicketTypeTemplate, BillingMode } from '../types/ticketTypeTemplate';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CACHE_KEY = 'ticket_templates_cache_v1';
const TTL_MS = 86_400_000; // 24 hours
const SCHEMA_VERSION = 1;
const API_URL = '/api/ticket-type-templates';
const CACHE_CLEAR_URL = '/api/ticket-type-templates/cache/clear';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CacheEnvelope {
  cachedAt: number;       // Unix ms
  ttlMs: number;
  schemaVersion: number;
  items: TicketTypeTemplate[];
}

export interface GetTicketTypeTemplatesOptions {
  /** Bypass cache and fetch from network */
  forceRefresh?: boolean;
  /** If true, return stale cache even when a refresh is attempted */
  allowStale?: boolean;
  /**
   * When supplied, the server merges this customer's active overrides
   * into the returned catalog. Per-customer reads bypass the local
   * cache entirely (overrides change rarely but per-customer caching
   * would 30× the cache footprint for marginal benefit).
   */
  customerId?: string;
}

export interface GetTicketTypeTemplatesResult {
  items: TicketTypeTemplate[];
  isStale?: boolean;
  warning?: string;
}

export interface RefreshResult {
  success: boolean;
  items?: TicketTypeTemplate[];
  error?: string;
}

export interface InvalidateResult {
  success: boolean;
  serverCleared: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Module-level state (in-memory tier)
// ---------------------------------------------------------------------------

let memoryCache: CacheEnvelope | null = null;
let inflightRequest: Promise<GetTicketTypeTemplatesResult> | null = null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isFresh(envelope: CacheEnvelope): boolean {
  return Date.now() - envelope.cachedAt < envelope.ttlMs;
}

function readLocalStorage(): CacheEnvelope | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CacheEnvelope = JSON.parse(raw);
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    if (!Array.isArray(parsed.items) || !parsed.cachedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalStorage(envelope: CacheEnvelope): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // localStorage full or unavailable – silent fallback to memory-only
  }
}

/**
 * Maps a raw API template object to TicketTypeTemplate.
 * Handles both the full format (ticketTypeId, displayName, group, …) and
 * the simplified catalog format (id, name, category, deliveryMode, …).
 */
function normaliseTemplate(raw: Record<string, any>): TicketTypeTemplate {
  if (raw.ticketTypeId) return raw as TicketTypeTemplate;

  const DELIVERY_BILLING: Record<string, BillingMode> = {
    Remote: 'hours',
    Onsite: 'hours',
    Instore: 'hours',
    Automation: 'units',
  };

  return {
    ticketTypeId: raw.id ?? '',
    displayName: raw.name ?? raw.id ?? '',
    group: raw.category ?? 'Other',
    serviceKey: raw.serviceKey ?? 'other',
    billingMode: DELIVERY_BILLING[raw.deliveryMode] ?? 'hours',
    billingStrategy: 'none',
    templateVersion: '1.0',
    subjectTemplate: raw.description,
  };
}

function normalizeResponse(data: Record<string, unknown>): TicketTypeTemplate[] {
  const raw = data.items ?? data.templates;
  const items: Record<string, any>[] = Array.isArray(raw) ? raw : [];
  if (items.length === 0 && Array.isArray(data)) {
    return (data as Record<string, any>[]).map(normaliseTemplate);
  }
  return items.map(normaliseTemplate);
}

function buildEnvelope(items: TicketTypeTemplate[]): CacheEnvelope {
  return {
    cachedAt: Date.now(),
    ttlMs: TTL_MS,
    schemaVersion: SCHEMA_VERSION,
    items,
  };
}

function storeEnvelope(envelope: CacheEnvelope): void {
  memoryCache = envelope;
  writeLocalStorage(envelope);
}

/** Resolve the best available cached envelope (memory → localStorage). */
function resolveCache(): CacheEnvelope | null {
  if (memoryCache) return memoryCache;
  const stored = readLocalStorage();
  if (stored) {
    memoryCache = stored; // promote to memory
  }
  return stored;
}

async function fetchFromNetwork(
  bypassHttpCache = false,
  customerId?: string,
): Promise<TicketTypeTemplate[]> {
  const url = customerId
    ? `${API_URL}?customerId=${encodeURIComponent(customerId)}`
    : API_URL;
  const response = await fetch(url, bypassHttpCache ? { cache: 'no-store' } : {});
  if (!response.ok) {
    throw new Error(`Template fetch failed: ${response.status}`);
  }
  const data = await response.json();
  return normalizeResponse(data);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch ticket type templates using two-tier cache with stale fallback.
 *
 * Policy:
 * - Fresh cache → return immediately (no network)
 * - Expired cache → try network; on failure return stale with warning
 * - No cache → fetch from network; throw if network also fails
 *
 * Concurrent calls share a single in-flight request.
 */
export async function getTicketTypeTemplates(
  options?: GetTicketTypeTemplatesOptions,
): Promise<GetTicketTypeTemplatesResult> {
  const { forceRefresh = false, allowStale = true, customerId } = options ?? {};

  // Per-customer reads always hit the network — the local cache only
  // holds the global catalog. Concurrent dedup also doesn't apply
  // because each customerId is its own logical request.
  if (customerId) {
    const items = await fetchFromNetwork(true, customerId);
    return { items };
  }

  // --- Fast path: fresh in-memory / localStorage cache ---
  if (!forceRefresh) {
    const cached = resolveCache();
    if (cached && isFresh(cached)) {
      return { items: cached.items };
    }
  }

  // --- Deduplicate concurrent network requests ---
  if (inflightRequest && !forceRefresh) {
    return inflightRequest;
  }

  const networkPromise = (async (): Promise<GetTicketTypeTemplatesResult> => {
    try {
      const items = await fetchFromNetwork(forceRefresh);
      const envelope = buildEnvelope(items);
      storeEnvelope(envelope);
      return { items };
    } catch (err: any) {
      // Stale fallback
      if (allowStale) {
        const stale = resolveCache();
        if (stale) {
          return {
            items: stale.items,
            isStale: true,
            warning: `Using cached templates (network error: ${err.message})`,
          };
        }
      }
      throw err;
    } finally {
      inflightRequest = null;
    }
  })();

  inflightRequest = networkPromise;
  return networkPromise;
}

/**
 * Force-refresh the cache. Designed for a "Refresh Templates" button.
 * Clears the server-side cache first to ensure the API returns fresh data.
 * On failure the existing cache is preserved.
 */
export async function refreshTicketTypeTemplatesCache(): Promise<RefreshResult> {
  try {
    // Clear server-side cache before fetching so the API returns fresh data
    try {
      await fetch(CACHE_CLEAR_URL, { method: 'POST' });
    } catch {
      // Server cache clear is best-effort — continue with fetch
    }

    const items = await fetchFromNetwork(true);
    const envelope = buildEnvelope(items);
    storeEnvelope(envelope);
    return { success: true, items };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Clear both client-side and server-side caches.
 */
export async function invalidateTicketTypeTemplatesCache(): Promise<InvalidateResult> {
  // Clear client-side caches
  memoryCache = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }

  // Clear server-side cache
  try {
    const res = await fetch(CACHE_CLEAR_URL, { method: 'POST' });
    const data = await res.json();
    return { success: true, serverCleared: data.cleared };
  } catch (error: any) {
    // Client cache is still cleared even if server call fails
    return { success: true, serverCleared: false, error: error.message };
  }
}

// ---------------------------------------------------------------------------
// Cache metadata (used by DebugPanel)
// ---------------------------------------------------------------------------

export function getTemplateCacheMetadata(): {
  cachedAt: number;
  ttlMs: number;
  recordCount: number;
} | null {
  const envelope = resolveCache();
  if (!envelope) return null;
  return {
    cachedAt: envelope.cachedAt,
    ttlMs: envelope.ttlMs,
    recordCount: envelope.items.length,
  };
}

// ---------------------------------------------------------------------------
// Test helpers (exported for unit tests only)
// ---------------------------------------------------------------------------

/** @internal Reset all module state for tests (memory + localStorage). */
export function _resetForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/** @internal Clear only the in-memory cache (simulates page reload). */
export function _clearMemoryForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
}
